import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PercentIcon from "@mui/icons-material/Percent";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

import { DocumentDTO } from "../../types/DocumentDTO";
import { useMpkDimensions } from "../../hooks/useMpkDimensions";
import type { MpkRow } from "../../hooks/useMpkDimensions";
import {
  saveDocumentMPK,
  type MPKDimensionDefinition,
} from "../../api/mpk";
import type { PurchaseLineDTO } from "../../api/documents";
import {
  hasRequiredPurchaseLineAmounts,
  isOptionalSettlementLine,
  isOptionalSettlementMpkRow,
} from "../../utils/settlementMpk";
import { formatCurrencyAmount } from "../../utils/formatters";
import ReadonlyTableSection from "../common/ReadonlyTableSection";
import DocumentPdfPreview from "./DocumentPdfPreview";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { useAuth } from "../../context/AuthContext";

type Props = {
  document: DocumentDTO;
  companyId: string;
  mpk: ReturnType<typeof useMpkDimensions>;
};

type MpkVisualRow = Pick<
  MpkRow,
  | "id"
  | "purchaseLineNo"
  | "amount"
  | "percent"
  | "purchaseCost"
  | "dimensionValues"
>;

const PURCHASE_COST_OPTIONS = [
  { value: "Cost", label: "Koszt" },
  { value: "Purchase", label: "Zakup" },
];

const PURCHASE_COST_LABELS: Record<string, string> = {
  Cost: "Koszt",
  Purchase: "Zakup",
};

/* ================================================================
 *  TYMCZASOWA BLOKADA WIZUALNA MPK
 * ================================================================
 * Cel zmiany:
 * - globalnie zablokować zapis MPK w UI, jeżeli użytkownik nie uzupełni
 *   wszystkich aktywnych wymiarów w każdym wierszu MPK.
 *
 * Ważne:
 * - to NIE zmienia payloadu wysyłanego do backendu,
 * - to NIE zmienia hooka useMpkDimensions,
 * - to NIE zmienia obecnego mechanizmu MPK,
 * - to jest tylko dodatkowy, tymczasowy warunek wizualny w tym komponencie.
 *
 * Usunięcie zmiany w przyszłości:
 * - ustaw TEMP_REQUIRE_ALL_MPK_DIMENSIONS na false
 *   albo usuń ten blok oraz wszystkie miejsca oznaczone komentarzem:
 *   "TYMCZASOWA BLOKADA WIZUALNA MPK".
 */
const TEMP_REQUIRE_ALL_MPK_DIMENSIONS = true;

function isBlankMpkValue(value: unknown) {
  return !value || String(value).trim() === "";
}

function hasNoFilledMpkDimension(
  row: MpkVisualRow,
  activeDimensions: MPKDimensionDefinition[]
) {
  return activeDimensions.every((dimension) =>
    isBlankMpkValue(row.dimensionValues[dimension.key])
  );
}

function hasMissingMpkDimension(
  row: MpkVisualRow,
  activeDimensions: MPKDimensionDefinition[]
) {
  return activeDimensions.some((dimension) =>
    isBlankMpkValue(row.dimensionValues[dimension.key])
  );
}
/* ================================================================
 *  KONIEC TYMCZASOWEJ BLOKADY WIZUALNEJ MPK
 * ================================================================ */

function makeLocalId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random()}`;
}

function roundMoney(value: number) {
  return Number(value.toFixed(2));
}

function calculateTotalAmount(rows: MpkVisualRow[]) {
  return Number(
    rows.reduce((sum, row) => sum + Number(row.amount || 0), 0).toFixed(2)
  );
}

function isAlmostEqual(a: number, b: number, precision = 0.01) {
  return Math.abs(a - b) <= precision;
}

function getPurchaseLineAmount(
  purchaseLines: PurchaseLineDTO[],
  purchaseLineNo?: number
) {
  if (purchaseLineNo === undefined) return 0;
  return Number(
    purchaseLines.find((line) => line.lineNo === purchaseLineNo)?.amount || 0
  );
}

function getPurchaseLineLabel(line?: PurchaseLineDTO) {
  if (!line) return "—";
  const description = line.description?.trim() || `Linia ${line.lineNo ?? "—"}`;
  return line.lineNo !== null ? `${description}` : description;
}

function getLineAllocationValidation(
  rows: MpkVisualRow[],
  purchaseLines: PurchaseLineDTO[],
  settlementDimensionsOptional = false
) {
  const amountByLine = new Map<number, number>();
  const percentByLine = new Map<number, number>();

  rows.forEach((row) => {
    if (row.purchaseLineNo === undefined) return;
    amountByLine.set(
      row.purchaseLineNo,
      roundMoney((amountByLine.get(row.purchaseLineNo) ?? 0) + Number(row.amount || 0))
    );
    percentByLine.set(
      row.purchaseLineNo,
      roundMoney((percentByLine.get(row.purchaseLineNo) ?? 0) + Number(row.percent || 0))
    );
  });

  const relevantLines = purchaseLines.filter(
    (line): line is PurchaseLineDTO & { lineNo: number } =>
      line.lineNo !== null && Math.abs(Number(line.amount || 0)) > 0.01 &&
      !isOptionalSettlementLine(line, settlementDimensionsOptional)
  );

  const invalidLineNos = relevantLines
    .filter((line) => {
      const target = Number(line.amount || 0);
      return (
        !isAlmostEqual(amountByLine.get(line.lineNo) ?? 0, target) ||
        !isAlmostEqual(percentByLine.get(line.lineNo) ?? 0, 100)
      );
    })
    .map((line) => line.lineNo);

  return {
    complete: invalidLineNos.length === 0,
    invalidLineNos,
    completeLinesCount: relevantLines.length - invalidLineNos.length,
    relevantLinesCount: relevantLines.length,
  };
}

function getStateCopy(state: "complete" | "over" | "draft" | "error") {
  switch (state) {
    case "complete":
      return {
        label: "Gotowe do zapisu",
        chipColor: "success" as const,
        icon: <CheckCircleOutlineIcon fontSize="small" />,
      };
    case "over":
      return {
        label: "Przekroczono limit",
        chipColor: "error" as const,
        icon: <ErrorOutlineIcon fontSize="small" />,
      };
    case "error":
      return {
        label: "Wymagane poprawki",
        chipColor: "error" as const,
        icon: <ErrorOutlineIcon fontSize="small" />,
      };
    default:
      return {
        label: "W trakcie opisu",
        chipColor: "warning" as const,
        icon: <ShieldOutlinedIcon fontSize="small" />,
      };
  }
}

function createEmptyRow(): MpkVisualRow {
  return {
    id: makeLocalId(),
    purchaseLineNo: undefined,
    amount: 0,
    percent: 0,
    purchaseCost: undefined,
    dimensionValues: {},
  };
}

export default function DocumentMpkV2Preview({
  document,
  companyId,
  mpk,
}: Props) {
  const { t } = useAppTranslation("common");
  const { user } = useAuth();

  const canApproveWithoutMpk =
    user?.role === "admin" ||
    user?.companyPolicy?.can_approve_without_mpk === true;

  const {
    rows,
    setRows,
    loading,
    editable,
    hasAnyDimension,
    activeDimensions,
    purchaseLines,
    settlementDimensionsOptional,
    requiredNetAmount,
  } = mpk;

  const documentNetAmount = requiredNetAmount;
  const canClearMpk = canApproveWithoutMpk || (
    settlementDimensionsOptional && purchaseLines.length > 0 &&
    !hasRequiredPurchaseLineAmounts(purchaseLines, settlementDimensionsOptional)
  );

  const normalizedRows = useMemo<MpkVisualRow[]>(() => {
    return rows.map((row) => ({
      id: row.id,
      purchaseLineNo: row.purchaseLineNo,
      amount: Number(row.amount || 0),
      percent: Number(row.percent || 0),
      purchaseCost: row.purchaseCost,
      dimensionValues: row.dimensionValues ?? {},
    }));
  }, [rows]);

  const [visualRows, setVisualRows] = useState<MpkVisualRow[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftRows, setDraftRows] = useState<MpkVisualRow[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setVisualRows(normalizedRows);
  }, [normalizedRows]);

  const openDialog = () => {
    if (!editable) return;

    const initialRows =
      visualRows.length > 0
        ? visualRows
        : canClearMpk
          ? []
          : [createEmptyRow()];

    setSaveError(null);
    setDraftRows(initialRows);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setSaveError(null);
  };

  const saveVisualChanges = async () => {
    const isClearingMpk = draftRows.length === 0;

    if (isClearingMpk && !canClearMpk) {
      setSaveError(
        t("Dodaj przynajmniej jeden wymiar przed zapisem.")
      );
      return;
    }

    const hasEmptyDimensionRows = draftRows.some((row) =>
      hasNoFilledMpkDimension(row, activeDimensions)
    );

    const hasMissingPurchaseLine = draftRows.some(
      (row) => row.purchaseLineNo === undefined
    );

    if (!isClearingMpk && hasMissingPurchaseLine) {
      setSaveError(t("Wybierz wiersz faktury dla każdej pozycji wymiarów."));
      return;
    }

    /* ================================================================
     *  TYMCZASOWA BLOKADA WIZUALNA MPK
     * ================================================================
     * Ten warunek blokuje zapis, jeżeli w którymkolwiek wierszu brakuje
     * dowolnego aktywnego wymiaru MPK.
     *
     * To zabezpieczenie jest celowo dodane także w funkcji zapisu,
     * żeby nie polegać wyłącznie na disabled przycisku "Zapisz MPK".
     */
    const hasIncompleteDimensionRows =
      TEMP_REQUIRE_ALL_MPK_DIMENSIONS &&
      draftRows.some((row) =>
        !isOptionalSettlementMpkRow(row.purchaseLineNo, purchaseLines, settlementDimensionsOptional) &&
        hasMissingMpkDimension(row, activeDimensions)
      );

    if (!isClearingMpk && hasIncompleteDimensionRows) {
      setSaveError(
        t("Uzupełnij wszystkie dostępne wymiary w każdej pozycji przed zapisem.")
      );
      return;
    }
    /* ================================================================
     *  KONIEC TYMCZASOWEJ BLOKADY WIZUALNEJ MPK
     * ================================================================ */

    if (!isClearingMpk && hasEmptyDimensionRows) {
      setSaveError(
        t("Każda pozycja musi mieć uzupełniony przynajmniej jeden wymiar.")
      );
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const payload = draftRows.map((row) => ({
        purchaseLineNo: row.purchaseLineNo as number,
        amount: row.amount,
        purchaseCost: row.purchaseCost,
        dimensions: activeDimensions
          .map((dimension) => {
            const dimensionValueCode = row.dimensionValues[dimension.key];
            if (!dimensionValueCode) return null;
            return { dimensionCode: dimension.code, dimensionValueCode };
          })
          .filter(Boolean) as {
          dimensionCode: string;
          dimensionValueCode: string;
        }[],
      }));

      await saveDocumentMPK(companyId, document.id, payload);

      const nextRows: MpkRow[] = draftRows.map((row, index) => ({
        id: row.id,
        purchaseLineNo: row.purchaseLineNo,
        amount: row.amount,
        percent: row.percent,
        purchaseCost: row.purchaseCost,
        dimensionValues: row.dimensionValues,
        dimensions: payload[index]?.dimensions ?? [],
      }));

      setRows(nextRows);
      setVisualRows(draftRows);
      setDialogOpen(false);
      setSnackbarOpen(true);
    } catch (e) {
      console.error(e);
      setSaveError(t("Nie udało się zapisać wymiarów"));
    } finally {
      setSaving(false);
    }
  };

  const totalAmount = calculateTotalAmount(visualRows.filter((row) =>
    !isOptionalSettlementMpkRow(row.purchaseLineNo, purchaseLines, settlementDimensionsOptional)
  ));
  const remainingAmount = roundMoney(documentNetAmount - totalAmount);
  const lineAllocation = getLineAllocationValidation(
    visualRows, purchaseLines, settlementDimensionsOptional
  );
  const allocationState =
    lineAllocation.complete && isAlmostEqual(totalAmount, documentNetAmount)
      ? "complete"
      : totalAmount > documentNetAmount + 0.01
        ? "over"
        : "draft";

  if (!hasAnyDimension && !loading) {
    return (
      <ReadonlyTableSection
        title="Dodatkowe wymiary i MPK"
        loading={false}
        empty={false}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <AccountTreeOutlinedIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {t("Dodatkowe wymiary nie zostały jeszcze zdefiniowane.")}
          </Typography>
        </Stack>
      </ReadonlyTableSection>
    );
  }

  return (
    <>
      <ReadonlyTableSection
        title="Dodatkowe wymiary i MPK"
        loading={loading}
        empty={!loading && visualRows.length === 0}
        emptyText="Brak zdefiniowanych wymiarów."
        actions={
          editable && (
            <Button
              size="small"
              variant={visualRows.length > 0 ? "outlined" : "contained"}
              startIcon={visualRows.length > 0 ? <EditIcon /> : <AddIcon />}
              onClick={openDialog}
            >
              {visualRows.length > 0 ? t("Edytuj") : t("Dodaj")}
            </Button>
          )
        }
      >
        <Stack spacing={2}>
          {visualRows.length > 0 && (
            <MpkInlineSummary
              state={allocationState}
              rowsCount={visualRows.length}
              completeLinesCount={lineAllocation.completeLinesCount}
              purchaseLinesCount={lineAllocation.relevantLinesCount}
              totalAmount={totalAmount}
              remainingAmount={remainingAmount}
              currency={document.amounts.currency}
            />
          )}

          <MpkSummaryTable
            document={document}
            rows={visualRows}
            activeDimensions={activeDimensions}
            purchaseLines={purchaseLines}
            editable={editable}
            onEdit={openDialog}
          />
        </Stack>
      </ReadonlyTableSection>

      <MpkEditDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSave={saveVisualChanges}
        document={document}
        companyId={companyId}
        rows={draftRows}
        setRows={setDraftRows}
        activeDimensions={activeDimensions}
        purchaseLines={purchaseLines}
        saving={saving}
        saveError={saveError}
        canApproveWithoutMpk={canClearMpk}
        settlementDimensionsOptional={settlementDimensionsOptional}
        requiredNetAmount={requiredNetAmount}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
        >
          {t("Wymiary zapisane pomyślnie")}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ================================================================
 *  READONLY TABLE
 * ================================================================ */

function MpkInlineSummary({
  state,
  rowsCount,
  completeLinesCount,
  purchaseLinesCount,
  totalAmount,
  remainingAmount,
  currency,
}: {
  state: "complete" | "over" | "draft";
  rowsCount: number;
  completeLinesCount: number;
  purchaseLinesCount: number;
  totalAmount: number;
  remainingAmount: number;
  currency: string;
}) {
  const { t } = useAppTranslation("common");
  // const copy = getStateCopy(state);

  return (
    <Box sx={{ p: 1.5, backgroundColor: "grey.50", borderRadius: 1 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {t("Pozycje")}: {rowsCount}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {t("Opisane linie")}: <strong>{completeLinesCount}/{purchaseLinesCount}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("Suma")}: <strong>{formatCurrencyAmount(totalAmount)} {currency}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("Pozostało")}: <strong>{formatCurrencyAmount(remainingAmount)} {currency}</strong>
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

function MpkSummaryTable({
  document,
  rows,
  activeDimensions,
  purchaseLines,
  editable,
  onEdit,
}: {
  document: DocumentDTO;
  rows: MpkVisualRow[];
  activeDimensions: MPKDimensionDefinition[];
  purchaseLines: PurchaseLineDTO[];
  editable: boolean;
  onEdit: () => void;
}) {
  const { t } = useAppTranslation("common");

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("Brak zdefiniowanych wymiarów.")}
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ overflow: "auto" }}>
      <Table size="small">
        <TableHead sx={{ backgroundColor: "grey.100" }}>
          <TableRow>
            <TableCell sx={{ minWidth: 220 }}>{t("Wiersz faktury")}</TableCell>
            <TableCell>{t("Rodzaj")}</TableCell>
            {activeDimensions.map((dimension) => (
              <TableCell key={dimension.key}>
                {dimension.name || dimension.code}
              </TableCell>
            ))}
            <TableCell align="right">{t("Kwota netto")}</TableCell>
            <TableCell align="right">{t("% udział")}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              sx={{ "&:nth-of-type(odd)": { backgroundColor: "grey.50" } }}
            >
              <TableCell>
                {getPurchaseLineLabel(
                  purchaseLines.find((line) => line.lineNo === row.purchaseLineNo)
                )}
              </TableCell>

              <TableCell>
                {row.purchaseCost ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t(PURCHASE_COST_LABELS[row.purchaseCost] ?? row.purchaseCost)}
                  />
                ) : (
                  "—"
                )}
              </TableCell>

              {activeDimensions.map((dimension) => {
                const valueCode = row.dimensionValues[dimension.key];
                const selected = dimension.values.find(
                  (option) => option.code === valueCode
                );
                return (
                  <TableCell key={`${row.id}-${dimension.key}`}>
                    {valueCode ? (
                      <>
                        <Typography variant="body2" fontWeight={600}>
                          {selected?.name ?? valueCode}
                        </Typography>
                        {/* <Typography variant="caption" color="text.secondary">
                          {valueCode}
                        </Typography> */}
                      </>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                );
              })}

              <TableCell align="right">
                {formatCurrencyAmount(row.amount)} {document.amounts.currency}
              </TableCell>

              <TableCell align="right">
                {Number(row.percent || 0).toFixed(2)}%
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ================================================================
 *  EDIT DIALOG
 * ================================================================ */

function MpkEditDialog({
  open,
  onClose,
  onSave,
  document,
  companyId,
  rows,
  setRows,
  activeDimensions,
  purchaseLines,
  saving,
  saveError,
  canApproveWithoutMpk,
  settlementDimensionsOptional,
  requiredNetAmount,
}: {
  open: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  document: DocumentDTO;
  companyId: string;
  rows: MpkVisualRow[];
  setRows: Dispatch<SetStateAction<MpkVisualRow[]>>;
  activeDimensions: MPKDimensionDefinition[];
  purchaseLines: PurchaseLineDTO[];
  saving: boolean;
  saveError: string | null;
  canApproveWithoutMpk: boolean;
  settlementDimensionsOptional: boolean;
  requiredNetAmount: number;
}) {
  const { t } = useAppTranslation("common");

  const documentNetAmount = requiredNetAmount;

  const totalAmount = calculateTotalAmount(rows.filter((row) =>
    !isOptionalSettlementMpkRow(row.purchaseLineNo, purchaseLines, settlementDimensionsOptional)
  ));
  const remainingAmount = roundMoney(documentNetAmount - totalAmount);

  const hasMissingPurchaseLine = rows.some(
    (row) => row.purchaseLineNo === undefined
  );
  const hasMissingPurchaseCost = rows.some((row) => !row.purchaseCost);
  const lineAllocation = useMemo(
    () => getLineAllocationValidation(rows, purchaseLines, settlementDimensionsOptional),
    [rows, purchaseLines, settlementDimensionsOptional]
  );
  const isPercentValid = lineAllocation.complete;
  const isAmountValid = isAlmostEqual(totalAmount, documentNetAmount);
  const hasRows = rows.length > 0;
  const isClearingMpk = rows.length === 0;
  const allocationState: "complete" | "over" | "draft" =
    isPercentValid && isAmountValid
      ? "complete"
      : totalAmount > documentNetAmount + 0.01
        ? "over"
        : "draft";

  const hasDuplicateRows = useMemo(() => {
    if (rows.length <= 1) return false;
    const keys = rows.map((row) =>
      `${row.purchaseLineNo ?? ""}|${activeDimensions
        .map((dimension) => row.dimensionValues[dimension.key] || "")
        .join("|")}`
    );
    return new Set(keys).size !== keys.length;
  }, [rows, activeDimensions]);

  const hasEmptyDimensionRows = useMemo(() => {
    return rows.some((row) =>
      hasNoFilledMpkDimension(row, activeDimensions)
    );
  }, [rows, activeDimensions]);

  /* ================================================================
   *  TYMCZASOWA BLOKADA WIZUALNA MPK
   * ================================================================
   * Ten warunek jest ostrzejszy niż obecny mechanizm MPK.
   * Obecny mechanizm wymaga minimum jednego wymiaru w wierszu.
   * Tymczasowa blokada wymaga wszystkich aktywnych wymiarów w wierszu.
   */
  const hasIncompleteDimensionRows = useMemo(() => {
    if (!TEMP_REQUIRE_ALL_MPK_DIMENSIONS) return false;

    return rows.some((row) =>
      !isOptionalSettlementMpkRow(row.purchaseLineNo, purchaseLines, settlementDimensionsOptional) &&
      hasMissingMpkDimension(row, activeDimensions)
    );
  }, [rows, activeDimensions, purchaseLines, settlementDimensionsOptional]);
  /* ================================================================
   *  KONIEC TYMCZASOWEJ BLOKADY WIZUALNEJ MPK
   * ================================================================ */

  const canSave =
    isClearingMpk
      ? canApproveWithoutMpk
      : !hasMissingPurchaseLine &&
        !hasMissingPurchaseCost &&
        !hasEmptyDimensionRows &&
        /* TYMCZASOWA BLOKADA WIZUALNA MPK */
        !hasIncompleteDimensionRows &&
        isPercentValid &&
        isAmountValid &&
        !hasDuplicateRows;

  // Comprehensive dialog state that accounts for all validation issues
  const dialogState: "complete" | "over" | "error" | "draft" = useMemo(() => {
    if (!hasRows) return "draft";
    if (allocationState === "over") return "over";
    if (
      hasDuplicateRows ||
      hasEmptyDimensionRows ||
      /* TYMCZASOWA BLOKADA WIZUALNA MPK */
      hasIncompleteDimensionRows ||
      hasMissingPurchaseLine ||
      hasMissingPurchaseCost
    ) return "error";
    if (!isPercentValid || !isAmountValid) return "draft";
    return "complete";
  }, [
    hasRows,
    allocationState,
    hasDuplicateRows,
    hasEmptyDimensionRows,
    hasIncompleteDimensionRows,
    hasMissingPurchaseLine,
    hasMissingPurchaseCost,
    isPercentValid,
    isAmountValid,
  ]);

  const stateCopy = getStateCopy(dialogState);

  const validationErrors = useMemo(() => {
    const errors: string[] = [];

    if (!hasRows && !canApproveWithoutMpk) {
      errors.push(t("Dodaj przynajmniej jedną pozycję wymiarów."));
    }

    if (hasDuplicateRows)
      errors.push(t("W obrębie jednej linii faktury kombinacja wymiarów musi być unikalna."));

    if (hasMissingPurchaseLine)
      errors.push(t("Wybierz wiersz faktury dla każdej pozycji."));

    /* ================================================================
     *  TYMCZASOWA BLOKADA WIZUALNA MPK
     * ================================================================
     * Przy aktywnej fladze pokazujemy użytkownikowi komunikat o konieczności
     * uzupełnienia wszystkich wymiarów. Nie pokazujemy wtedy starego komunikatu
     * o minimum jednym wymiarze, żeby nie dublować i nie mieszać informacji.
     */
    if (hasIncompleteDimensionRows) {
      errors.push(
        t("Uzupełnij wszystkie dostępne wymiary w każdej pozycji przed zapisem.")
      );
    } else if (hasEmptyDimensionRows) {
      errors.push(
        t("Każda pozycja musi mieć uzupełniony przynajmniej jeden wymiar.")
      );
    }
    /* ================================================================
     *  KONIEC TYMCZASOWEJ BLOKADY WIZUALNEJ MPK
     * ================================================================ */

    if (hasMissingPurchaseCost)
      errors.push(t("Uzupełnij pole Koszt / Zakup dla każdej pozycji."));

    if (!isPercentValid && hasRows)
      errors.push(t("Każda linia faktury musi być rozpisana do 100% swojej kwoty netto."));

    if (!isAmountValid && hasRows)
      errors.push(t("Suma kwot musi być równa kwocie netto dokumentu."));

    return errors;
  }, [
    hasRows,
    canApproveWithoutMpk,
    hasDuplicateRows,
    hasEmptyDimensionRows,
    hasIncompleteDimensionRows,
    hasMissingPurchaseLine,
    hasMissingPurchaseCost,
    isPercentValid,
    isAmountValid,
    t,
  ]);

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const updateRow = (id: string, patch: Partial<MpkVisualRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  };

  const updateDimensionValue = (
    rowId: string,
    dimensionKey: string,
    value?: string
  ) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? { ...row, dimensionValues: { ...row.dimensionValues, [dimensionKey]: value } }
          : row
      )
    );
  };

  const updatePurchaseLine = (id: string, purchaseLineNo?: number) => {
    setRows((prev) => {
      const targetAmount = getPurchaseLineAmount(purchaseLines, purchaseLineNo);
      const alreadyAllocated = prev
        .filter((row) => row.id !== id && row.purchaseLineNo === purchaseLineNo)
        .reduce((sum, row) => sum + Number(row.amount || 0), 0);
      const remaining = roundMoney(targetAmount - alreadyAllocated);
      const percent = Math.abs(targetAmount) > 0.01
        ? Number(((remaining / targetAmount) * 100).toFixed(2))
        : 0;

      return prev.map((row) =>
        row.id === id
          ? { ...row, purchaseLineNo, amount: remaining, percent }
          : row
      );
    });
  };

  const updateAmount = (id: string, amount: number) => {
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    const row = rows.find((item) => item.id === id);
    const purchaseLineAmount = getPurchaseLineAmount(
      purchaseLines,
      row?.purchaseLineNo
    );
    const percent =
      Math.abs(purchaseLineAmount) > 0.01
        ? Number(((safeAmount / purchaseLineAmount) * 100).toFixed(2))
        : 0;
    updateRow(id, { amount: safeAmount, percent });
  };

  const updatePercent = (id: string, percent: number) => {
    const safePercent = Number.isFinite(percent) ? percent : 0;
    const row = rows.find((item) => item.id === id);
    const purchaseLineAmount = getPurchaseLineAmount(
      purchaseLines,
      row?.purchaseLineNo
    );
    const amount = roundMoney((safePercent / 100) * purchaseLineAmount);
    updateRow(id, { percent: safePercent, amount });
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: { xs: 2, md: 3 },
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="h6" fontWeight={600} noWrap>
              {t("Edycja wymiarów i MPK")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary" noWrap>
              {document.externalNumber || document.number}
            </Typography>
            <Typography variant="body2" color="text.disabled">·</Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {formatCurrencyAmount(documentNetAmount)} {document.amounts.currency} {settlementDimensionsOptional ? t("do rozliczenia MPK") : t("netto")}
            </Typography>
          </Stack>
        </Box>

        <IconButton onClick={onClose} disabled={saving} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0, 1.04fr) minmax(420px, 0.96fr)",
            },
            height: "100%",
            minHeight: "calc(100vh - 136px)",
          }}
        >
          {/* LEFT COLUMN – EDITOR */}
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              overflow: "auto",
              borderRight: { xs: "none", lg: "1px solid" },
              borderColor: "divider",
            }}
          >
            <Stack spacing={2}>
              {/* CONTROL PANEL */}
              <Paper sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle1">
                    {t("Panel kontroli")}
                  </Typography>
                  <Chip
                    size="small"
                    icon={stateCopy.icon}
                    label={t(stateCopy.label)}
                    color={stateCopy.chipColor}
                    variant="outlined"
                  />
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      xl: "repeat(4, 1fr)",
                    },
                    gap: 1.5,
                  }}
                >
                  <MetricCard
                    icon={<ReceiptLongOutlinedIcon fontSize="small" />}
                    label={settlementDimensionsOptional ? t("Kwota do rozliczenia MPK") : t("Kwota netto")}
                    value={`${formatCurrencyAmount(documentNetAmount)} ${document.amounts.currency}`}
                  />
                  <MetricCard
                    icon={<AttachMoneyIcon fontSize="small" />}
                    label={t("Suma")}
                    value={`${formatCurrencyAmount(totalAmount)} ${document.amounts.currency}`}
                  />
                  <MetricCard
                    icon={<ShieldOutlinedIcon fontSize="small" />}
                    label={t("Pozostało")}
                    value={`${formatCurrencyAmount(remainingAmount)} ${document.amounts.currency}`}
                  />
                  <MetricCard
                    icon={<PercentIcon fontSize="small" />}
                    label={t("Opisane linie")}
                    value={`${lineAllocation.completeLinesCount}/${lineAllocation.relevantLinesCount}`}
                  />
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t("Postęp alokacji")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {lineAllocation.completeLinesCount} / {lineAllocation.relevantLinesCount}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={
                      lineAllocation.relevantLinesCount > 0
                        ? Math.min(
                            (lineAllocation.completeLinesCount /
                              lineAllocation.relevantLinesCount) * 100,
                            100
                          )
                        : 0
                    }
                    color={
                      allocationState === "complete"
                        ? "success"
                        : allocationState === "over"
                          ? "error"
                          : "primary"
                    }
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              </Paper>

              {/* VALIDATION ERRORS */}
              {validationErrors.length > 0 && (
                <Alert
                  severity={hasDuplicateRows || allocationState === "over" ? "error" : "warning"}
                  variant="outlined"
                >
                  <AlertTitle sx={{ fontWeight: 600 }}>
                    {t("Wymagane poprawki przed zapisem")}
                  </AlertTitle>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {validationErrors.map((msg, i) => (
                      <li key={i}>
                        <Typography variant="body2">{msg}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}

              {saveError && (
                <Alert severity="error" variant="outlined">
                  {saveError}
                </Alert>
              )}

              {rows.length === 0 && canApproveWithoutMpk && (
                <Alert severity="warning" variant="outlined">
                  <AlertTitle sx={{ fontWeight: 600 }}>
                    {t("Zapis pustej tabeli")}
                  </AlertTitle>
                  <Typography variant="body2">
                    {t("Masz uprawnienie do zapisania dokumentu bez określenia wymiarów. Po zapisaniu tabela zostanie wyczyszczona.")}
                  </Typography>
                </Alert>
              )}

              {/* ROWS HEADER */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Typography variant="subtitle1">
                  {t("Pozycje opisu dokumentu")}
                </Typography>
                <Chip
                  size="small"
                  label={`${rows.length} ${t("pozycji")}`}
                  variant="outlined"
                  color="primary"
                />
              </Stack>

              {/* ROW ACCORDIONS */}
              <Stack spacing={1.5}>
                {rows.map((row, index) => {
                  const selectedPurchaseLine = purchaseLines.find(
                    (line) => line.lineNo === row.purchaseLineNo
                  );
                  const filledDimensions = activeDimensions.filter(
                    (dimension) => !!row.dimensionValues[dimension.key]
                  ).length;
                  const rowHasAnyDimension = filledDimensions > 0;
                  // const rowSummary = activeDimensions
                  //   .map((dimension) => {
                  //     const valueCode = row.dimensionValues[dimension.key];
                  //     if (!valueCode) return null;
                  //     const selected = dimension.values.find((option) => option.code === valueCode);
                  //     return selected?.code ?? valueCode;
                  //   })
                  //   .filter(Boolean)
                  //   .slice(0, 10)
                  //   .join(" · ");

                  return (
                    <Accordion
                      key={row.id}
                      defaultExpanded={rows.length === 1 || index === rows.length - 1}
                      disableGutters
                      elevation={0}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "8px !important",
                        overflow: "hidden",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ px: 2, minHeight: 56 }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          sx={{ width: "100%", pr: 2, minWidth: 0 }}
                        >
                          <Box minWidth={0} flex={1}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {t("Pozycja")} {index + 1}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              component="div"
                            >
                              {row.purchaseLineNo !== undefined
                                ? getPurchaseLineLabel(selectedPurchaseLine)
                                : t("Wybierz wiersz faktury")}
                            </Typography>
                            {/* <Typography variant="caption" color="text.secondary" noWrap component="div">
                              {rowSummary || t("Wybierz przynajmniej jeden wymiar")}
                            </Typography> */}
                          </Box>

                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${formatCurrencyAmount(row.amount)} ${document.amounts.currency}`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${Number(row.percent || 0).toFixed(2)}%`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              color={rowHasAnyDimension ? "primary" : "warning"}
                              label={`${filledDimensions}/${activeDimensions.length} ${t("wym.")}`}
                            />
                            {row.purchaseCost && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t(PURCHASE_COST_LABELS[row.purchaseCost] ?? row.purchaseCost)}
                              />
                            )}
                          </Stack>
                        </Stack>
                      </AccordionSummary>

                      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                        <Divider sx={{ mb: 2 }} />

                        <Stack spacing={2}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                md: "2fr 1fr 1fr 1fr",
                              },
                              gap: 1.5,
                            }}
                          >
                            <Autocomplete
                              size="small"
                              options={purchaseLines.filter((line) => line.lineNo !== null)}
                              value={selectedPurchaseLine ?? null}
                              disabled={saving}
                              onChange={(_, selected) =>
                                updatePurchaseLine(
                                  row.id,
                                  selected?.lineNo ?? undefined
                                )
                              }
                              getOptionLabel={(option) => getPurchaseLineLabel(option)}
                              isOptionEqualToValue={(option, value) =>
                                option.lineNo === value.lineNo
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={t("Wiersz faktury")}
                                  placeholder={t("Wybierz po opisie")}
                                />
                              )}
                            />

                            <TextField
                              select
                              size="small"
                              label={t("Koszt / Zakup")}
                              value={row.purchaseCost ?? ""}
                              disabled={saving}
                              onChange={(e) =>
                                updateRow(row.id, { purchaseCost: e.target.value })
                              }
                            >
                              {PURCHASE_COST_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {t(option.label)}
                                </MenuItem>
                              ))}
                            </TextField>

                            <TextField
                              size="small"
                              type="number"
                              label={t("Kwota netto")}
                              value={row.amount}
                              disabled={saving}
                              onFocus={(e) => e.target.select()}
                              inputProps={{ min: Number(selectedPurchaseLine?.amount || 0) < 0 ? undefined : 0, step: 0.01 }}
                              onChange={(e) => updateAmount(row.id, Number(e.target.value))}
                            />

                            <TextField
                              size="small"
                              type="number"
                              label={t("% udział")}
                              value={row.percent}
                              disabled={saving}
                              onFocus={(e) => e.target.select()}
                              inputProps={{ min: 0, step: 0.01 }}
                              onChange={(e) => updatePercent(row.id, Number(e.target.value))}
                            />
                          </Box>

                          {activeDimensions.length > 0 && (
                            <>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <AccountTreeOutlinedIcon fontSize="small" color="action" />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={600}
                                  sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
                                >
                                  {t("Wymiary")}
                                </Typography>
                              </Stack>

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                                  gap: 1.5,
                                }}
                              >
                                {activeDimensions.map((dimension) => {
                                  const selectedValue =
                                    dimension.values.find(
                                      (option) => option.code === row.dimensionValues[dimension.key]
                                    ) ?? null;

                                  return (
                                    <Autocomplete
                                      key={`${row.id}-${dimension.key}`}
                                      size="small"
                                      options={dimension.values}
                                      value={selectedValue}
                                      disabled={saving}
                                      onChange={(_, selected) =>
                                        updateDimensionValue(row.id, dimension.key, selected?.code)
                                      }
                                      getOptionLabel={(option) =>
                                        // option.name ? `${option.name} (${option.code})` : option.code
                                        option.name ? `${option.name}` : option.code
                                      }
                                      isOptionEqualToValue={(option, value) => option.code === value.code}
                                      renderInput={(params) => (
                                        <TextField {...params} label={dimension.name || dimension.code} />
                                      )}
                                    />
                                  );
                                })}
                              </Box>
                            </>
                          )}

                          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteOutlineIcon fontSize="small" />}
                              onClick={() => removeRow(row.id)}
                              disabled={saving}
                            >
                              {t("Usuń")}
                            </Button>
                          </Box>
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addRow}
                  disabled={saving}
                >
                  {t("Dodaj pozycję")}
                </Button>
              </Stack>
            </Stack>
          </Box>

          {/* RIGHT COLUMN – PDF */}
          <Box
            sx={{
              p: 2,
              overflow: "hidden",
              backgroundColor: "grey.50",
              display: { xs: "none", lg: "flex" },
              flexDirection: "column",
              height: "100%",
              "& > .MuiPaper-root": {
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              },
              "& > .MuiPaper-root > div:last-of-type": {
                flex: 1,
                height: "auto !important",
              },
            }}
          >
            <DocumentPdfPreview
              documentNumber={document.externalNumber || document.number || ""}
              documentId={document.id}
              companyId={companyId}
              ksefNo={document.ksefNo}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          justifyContent: "flex-end",
          gap: 1,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button onClick={onClose} disabled={saving}>
          {t("Anuluj")}
        </Button>

        <Button
          variant="contained"
          onClick={onSave}
          disabled={!canSave || saving}
        >
          {saving ? t("Zapisywanie...") : t("Zapisz")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ================================================================
 *  PRESENTATIONAL HELPERS
 * ================================================================ */

function MetricCard({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Box sx={{ p: 1.5, backgroundColor: "grey.50", borderRadius: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={600} noWrap>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}